import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ASHLEY_INTERFACE_OPEN_EVENT, ASHLEY_INTERFACE_CLOSED_EVENT } from "../helpers/ashleyInterfaceLauncher";
import { ASHLEY_PHASE } from "./ashleyInterfaceTypes";
import { buildAshleyOpeningGreeting } from "./speech/ashleyGreeting";
import { ASHLEY_PROMPTS } from "./speech/ashleyPrompts";
import { isAshleyConversationCancelPhrase } from "./speech/ashleyConversationCommands";
import {
  isAshleyAffirmationPhrase,
  isAshleyDeclinePhrase,
} from "./speech/ashleyFollowUpPhrases";
import { pickAshleyConversationClosing } from "./speech/ashleyClosingPhrases";
import { cancelAshleySpeech, isAshleySpeechSupported, speakAshley } from "./speech/ashleySpeech";
import {
  POST_GREETING_LISTEN_DELAY_MS,
  runAfterGreetingSpeech,
  runAfterVoiceAcknowledgement,
} from "./speech/voiceNavigationTiming";
import { useAshleyListening } from "./voice/recognition/useAshleyListening";
import { resolveVoiceIntent } from "./voice/intents/resolveVoiceIntent";
import { createVoiceRouter } from "./voice/router/createVoiceRouter";
import { createWebSpeechRecognitionAdapter } from "./voice/recognition/WebSpeechRecognitionAdapter";
import { isGuidedWorkflowActive } from "./forms/guidedWorkflowRegistry";
import { ashleyLog } from "./debug/ashleySttDebug";
import {
  captureEnvironmentSnapshot,
  msSinceLastGesture,
  voiceDiag,
  ASHLEY_BYPASS_TTS_DIAGNOSTIC,
} from "./debug/ashleyVoiceDiagnostics";
import { createGmailConversationState } from "../connectedServices/email/gmailConversationState";
import {
  applyFetchedEmailBody,
  handleGmailBrowseTurn,
} from "./speech/handleGmailConversationTurn";
import { EmailService } from "../connectedServices";
import {
  OPEN_SPAM_OFFER,
  formatEmailDetailSummary,
} from "../connectedServices/email/formatEmailVoiceSummary";

const AshleyInterfaceContext = createContext(null);

export function AshleyInterfaceProvider({ children, appDispatch, userDisplayName = null }) {
  const [phase, setPhase] = useState(ASHLEY_PHASE.IDLE);
  const [statusMessage, setStatusMessage] = useState("");
  const [speechPlaybackConfirmed, setSpeechPlaybackConfirmed] = useState(false);
  const isActiveRef = useRef(false);
  const postSpeechTimerRef = useRef(null);
  const pendingFollowUpRef = useRef(null);
  const router = useMemo(() => createVoiceRouter(appDispatch), [appDispatch]);

  const clearPostSpeechTimer = useCallback(() => {
    if (postSpeechTimerRef.current) {
      clearTimeout(postSpeechTimerRef.current);
      postSpeechTimerRef.current = null;
    }
  }, []);

  const closeAshleyInterface = useCallback(() => {
    const wasActive = isActiveRef.current;
    if (wasActive) {
      ashleyLog("Overlay closed");
    }
    isActiveRef.current = false;
    clearPostSpeechTimer();
    cancelAshleySpeech();
    listeningControlsRef.current?.abortListening();
    pendingFollowUpRef.current = null;
    setSpeechPlaybackConfirmed(false);
    setPhase(ASHLEY_PHASE.IDLE);
    setStatusMessage("");
    if (wasActive) {
      window.dispatchEvent(new CustomEvent(ASHLEY_INTERFACE_CLOSED_EVENT));
    }
  }, [clearPostSpeechTimer]);

  const listeningControlsRef = useRef(null);
  const promptHandoffRef = useRef(false);

  const handleSpeechStart = useCallback((source = "utterance.onstart") => {
    setSpeechPlaybackConfirmed(true);
    voiceDiag("Overlay speech playback confirmed", {
      hypothesisId: "H-C",
      location: "AshleyInterfaceProvider.handleSpeechStart",
      data: { source },
    });
  }, []);

  const finishWithSpeech = useCallback((message, { closeAfter = true, performAction } = {}) => {
    if (!isActiveRef.current) {
      return;
    }

    const finalize = () => {
      postSpeechTimerRef.current = null;
      performAction?.();
      if (closeAfter) {
        closeAshleyInterface();
      }
    };

    const complete = ({ deferNavigation = false } = {}) => {
      if (performAction) {
        clearPostSpeechTimer();
        postSpeechTimerRef.current = runAfterVoiceAcknowledgement(finalize, {
          defer: deferNavigation,
        });
        return;
      }

      finalize();
    };

    if (!isAshleySpeechSupported()) {
      setPhase(ASHLEY_PHASE.SPEAKING);
      setStatusMessage(message);
      complete();
      return;
    }

    setPhase(ASHLEY_PHASE.SPEAKING);
    setStatusMessage(message);

    speakAshley(message, {
      onStart: handleSpeechStart,
      onEnd: () => complete({ deferNavigation: true }),
    });
  }, [clearPostSpeechTimer, closeAshleyInterface, handleSpeechStart]);

  const resumeCommandListening = useCallback((source = "retry") => {
    if (!isActiveRef.current) {
      return;
    }

    voiceDiag("resumeCommandListening()", {
      hypothesisId: "H-CHAIN",
      location: "AshleyInterfaceProvider.resumeCommandListening",
      data: { source },
    });

    setPhase(ASHLEY_PHASE.LISTENING);
    setStatusMessage("Listening...");
    listeningControlsRef.current?.startListening();
  }, []);

  const speakAndResumeListening = useCallback(
    (message) => {
      if (!isActiveRef.current) {
        return;
      }

      const resume = () => {
        clearPostSpeechTimer();
        postSpeechTimerRef.current = runAfterGreetingSpeech(() => {
          postSpeechTimerRef.current = null;
          resumeCommandListening("unknown-retry");
        });
      };

      if (!isAshleySpeechSupported()) {
        setPhase(ASHLEY_PHASE.SPEAKING);
        setStatusMessage(message);
        resume();
        return;
      }

      setPhase(ASHLEY_PHASE.SPEAKING);
      setStatusMessage(message);

      speakAshley(message, {
        onStart: handleSpeechStart,
        onEnd: () => resume(),
      });
    },
    [clearPostSpeechTimer, handleSpeechStart, resumeCommandListening],
  );

  const shouldRetryUnknownCommand = useCallback((result) => {
    return (
      result?.code === "unknown_intent" || result?.code === "unhandled_intent"
    );
  }, []);

  const handleFinalTranscript = useCallback(async (transcript) => {
    if (!isActiveRef.current) {
      return;
    }

    if (isAshleyConversationCancelPhrase(transcript)) {
      closeAshleyInterface();
      return;
    }

    setPhase(ASHLEY_PHASE.ROUTING);
    setStatusMessage("Processing...");

    const pendingFollowUp = pendingFollowUpRef.current;
    if (pendingFollowUp) {
      const followUpKind =
        typeof pendingFollowUp === "string" ? pendingFollowUp : pendingFollowUp.kind;
      const followUpContext =
        typeof pendingFollowUp === "string" ? null : pendingFollowUp.context || null;

      if (followUpKind === "email_browse" || followUpKind === "email_review_all") {
        const gmailState = createGmailConversationState({
          ...(followUpContext || {}),
          browsingMode:
            followUpKind === "email_review_all"
              ? followUpContext?.browsingMode || "review_all"
              : followUpContext?.browsingMode || "browse",
        });

        let turn = handleGmailBrowseTurn(transcript, gmailState);

        if (turn.action === "fetch_detail" && turn.messageId) {
          const detail = await EmailService.getMessageDetail(turn.messageId);
          const bodyText = detail.success
            ? detail.bodyText || detail.messages?.[0]?.bodyText || ""
            : gmailState.selectedEmail?.snippet || "";
          turn = applyFetchedEmailBody(
            turn.state || gmailState,
            bodyText,
            { speakFull: Boolean(turn.pendingFullRead) },
          );
        }

        if (turn.action === "close") {
          pendingFollowUpRef.current = null;
          finishWithSpeech(pickAshleyConversationClosing());
          return;
        }

        if (turn.action === "open_inbox") {
          pendingFollowUpRef.current = null;
          const openResult = await EmailService.openInbox();
          if (openResult.message) {
            finishWithSpeech(openResult.message, {
              performAction: openResult.performAction,
            });
            return;
          }
          finishWithSpeech(pickAshleyConversationClosing());
          return;
        }

        if (turn.action === "open_message") {
          pendingFollowUpRef.current = {
            kind: "email_browse",
            context: turn.state || gmailState,
          };
          const openResult = await EmailService.openMessage(turn.messageId);
          if (openResult.message) {
            finishWithSpeech(openResult.message, {
              performAction: openResult.performAction,
            });
            return;
          }
          finishWithSpeech(pickAshleyConversationClosing());
          return;
        }

        if (turn.state) {
          pendingFollowUpRef.current = {
            kind:
              turn.state.browsingMode === "review_all"
                ? "email_review_all"
                : "email_browse",
            context: turn.state,
          };
        } else {
          pendingFollowUpRef.current = null;
        }

        if (turn.speech) {
          speakAndResumeListening(turn.speech);
        } else {
          speakAndResumeListening("What would you like to do?");
        }
        return;
      }

      if (followUpKind === "spam_attention") {
        const messages = Array.isArray(followUpContext?.messages)
          ? followUpContext.messages
          : [];

        if (isAshleyDeclinePhrase(transcript)) {
          pendingFollowUpRef.current = null;
          finishWithSpeech(pickAshleyConversationClosing());
          return;
        }

        if (isAshleyAffirmationPhrase(transcript)) {
          const detail = formatEmailDetailSummary(messages);
          pendingFollowUpRef.current = {
            kind: "open_spam_folder",
            context: null,
          };
          speakAndResumeListening(`${detail} ${OPEN_SPAM_OFFER}`);
          return;
        }

        speakAndResumeListening(
          "I can review those Spam messages, or we can finish here. What would you like?",
        );
        return;
      }

      if (followUpKind === "open_spam_folder") {
        if (isAshleyDeclinePhrase(transcript)) {
          pendingFollowUpRef.current = null;
          finishWithSpeech(pickAshleyConversationClosing());
          return;
        }

        if (isAshleyAffirmationPhrase(transcript)) {
          pendingFollowUpRef.current = null;
          const openResult = await EmailService.openSpam();
          if (openResult.message) {
            finishWithSpeech(openResult.message, {
              performAction: openResult.performAction,
            });
            return;
          }
          finishWithSpeech(pickAshleyConversationClosing());
          return;
        }

        speakAndResumeListening(
          "I can open your Spam folder, or we can finish here. What would you like?",
        );
        return;
      }

      if (isAshleyDeclinePhrase(transcript)) {
        pendingFollowUpRef.current = null;
        finishWithSpeech(pickAshleyConversationClosing());
        return;
      }

      if (
        followUpKind === "open_email_inbox" &&
        isAshleyAffirmationPhrase(transcript)
      ) {
        pendingFollowUpRef.current = null;
        const openResult = await EmailService.openInbox();
        if (openResult.message) {
          finishWithSpeech(openResult.message, {
            performAction: openResult.performAction,
          });
          return;
        }
        finishWithSpeech(pickAshleyConversationClosing());
        return;
      }

      // Unrecognized reply while a non-browse follow-up is pending — clear and fall through.
      pendingFollowUpRef.current = null;
    }

    const resolved = resolveVoiceIntent(transcript);
    if (!resolved) {
      speakAndResumeListening(ASHLEY_PROMPTS.UNKNOWN_COMMAND_RETRY);
      return;
    }

    const result = await router(resolved);

    if (process.env.NODE_ENV !== "production") {
      ashleyLog("Intent routed", {
        transcript,
        intentId: resolved.intentId,
        confidence: resolved.confidence,
        success: result.success,
        code: result.code,
      });
    }

    if (shouldRetryUnknownCommand(result)) {
      speakAndResumeListening(ASHLEY_PROMPTS.UNKNOWN_COMMAND_RETRY);
      return;
    }

    if (result.awaitFollowUp && result.message) {
      pendingFollowUpRef.current = {
        kind: result.awaitFollowUp,
        context: result.followUpContext || null,
      };
      speakAndResumeListening(result.message);
      return;
    }

    if (result.message) {
      finishWithSpeech(result.message, {
        performAction: result.performAction,
      });
      return;
    }

    closeAshleyInterface();
  }, [
    closeAshleyInterface,
    finishWithSpeech,
    router,
    shouldRetryUnknownCommand,
    speakAndResumeListening,
  ]);

  const handleListeningError = useCallback((code) => {
    if (!isActiveRef.current) {
      return;
    }

    voiceDiag(`Speech error: ${code}`, {
      hypothesisId: "H-F",
      location: "AshleyInterfaceProvider.handleListeningError",
      data: { code, msSinceGesture: msSinceLastGesture() },
    });

    if (code === "unsupported") {
      finishWithSpeech(ASHLEY_PROMPTS.UNSUPPORTED_BROWSER);
      return;
    }

    if (code === "not-allowed") {
      finishWithSpeech(ASHLEY_PROMPTS.MIC_DENIED);
      return;
    }

    if (code === "no-speech") {
      finishWithSpeech(ASHLEY_PROMPTS.NO_SPEECH);
      return;
    }

    finishWithSpeech(ASHLEY_PROMPTS.RECOGNITION_ERROR);
  }, [finishWithSpeech]);

  const listening = useAshleyListening({
    onFinalTranscript: handleFinalTranscript,
    onError: handleListeningError,
    onEnd: () => {
      if (isActiveRef.current && phase === ASHLEY_PHASE.LISTENING) {
        setStatusMessage("Listening...");
      }
    },
  });

  listeningControlsRef.current = listening;

  const proceedToListening = useCallback((source) => {
    if (promptHandoffRef.current) {
      voiceDiag(`CHAIN 5/8: proceedToListening() SKIPPED (already proceeded)`, {
        hypothesisId: "H-CHAIN",
        location: "AshleyInterfaceProvider.proceedToListening",
        data: { source },
      });
      return;
    }

    if (!isActiveRef.current) {
      voiceDiag(`CHAIN 5/8: proceedToListening() SKIPPED (inactive)`, {
        hypothesisId: "H-CHAIN",
        location: "AshleyInterfaceProvider.proceedToListening",
        data: { source },
      });
      return;
    }

    promptHandoffRef.current = true;
    const msSinceGesture = msSinceLastGesture();

    voiceDiag("CHAIN 5/8: proceedToListening()", {
      hypothesisId: "H-CHAIN",
      location: "AshleyInterfaceProvider.proceedToListening",
      data: { source, msSinceGesture },
    });

    if (msSinceGesture != null && msSinceGesture > 1000) {
      voiceDiag(
        `WARNING: startListening ${msSinceGesture}ms after gesture (may break Android mic)`,
        {
          hypothesisId: "H-D",
          location: "AshleyInterfaceProvider.proceedToListening",
          data: { msSinceGesture },
        },
      );
    }

    setPhase(ASHLEY_PHASE.LISTENING);
    setStatusMessage("Listening...");
    listeningControlsRef.current?.startListening();
  }, []);

  const beginPrompt = useCallback(() => {
    const openingGreeting = buildAshleyOpeningGreeting();

    voiceDiag("CHAIN 1/8: beginPrompt()", {
      hypothesisId: "H-CHAIN",
      location: "AshleyInterfaceProvider.beginPrompt",
      data: { msSinceGesture: msSinceLastGesture() },
    });

    const recognitionSupported = createWebSpeechRecognitionAdapter().isSupported();
    if (!recognitionSupported) {
      voiceDiag("SpeechRecognition unavailable — aborting voice flow", {
        hypothesisId: "H-E",
        location: "AshleyInterfaceProvider.beginPrompt",
      });
      setPhase(ASHLEY_PHASE.PROMPTING);
      finishWithSpeech(ASHLEY_PROMPTS.UNSUPPORTED_BROWSER);
      return;
    }

    if (ASHLEY_BYPASS_TTS_DIAGNOSTIC) {
      voiceDiag("DIAG: TTS bypass — overlay text only, skipping CHAIN 2–4", {
        hypothesisId: "H-CHAIN",
        location: "AshleyInterfaceProvider.beginPrompt",
        data: { msSinceGesture: msSinceLastGesture() },
      });
      setPhase(ASHLEY_PHASE.SPEAKING);
      setStatusMessage(openingGreeting);
      proceedToListening("tts-bypass-diagnostic");
      return;
    }

    if (!isAshleySpeechSupported()) {
      voiceDiag("TTS unsupported — aborting voice flow", {
        hypothesisId: "H-B",
        location: "AshleyInterfaceProvider.beginPrompt",
      });
      setPhase(ASHLEY_PHASE.PROMPTING);
      setStatusMessage(ASHLEY_PROMPTS.UNSUPPORTED_BROWSER);
      return;
    }

    voiceDiag("Prompt TTS starting", {
      hypothesisId: "H-C",
      location: "AshleyInterfaceProvider.beginPrompt",
      data: { msSinceGesture: msSinceLastGesture() },
    });

    setPhase(ASHLEY_PHASE.SPEAKING);
    setStatusMessage(openingGreeting);
    setSpeechPlaybackConfirmed(false);

    speakAshley(openingGreeting, {
      onStart: handleSpeechStart,
      onEnd: (source = "utterance.onend") => {
        ashleyLog("Prompt TTS finished — scheduling speech recognition", { source });
        clearPostSpeechTimer();
        postSpeechTimerRef.current = runAfterGreetingSpeech(() => {
          postSpeechTimerRef.current = null;
          proceedToListening(source);
        });
      },
      onError: (event) => {
        voiceDiag(`TTS error: ${event?.error || "unknown"}`, {
          hypothesisId: "H-C",
          location: "AshleyInterfaceProvider.beginPrompt.onError",
          data: { error: event?.error },
        });
      },
    });
  }, [clearPostSpeechTimer, finishWithSpeech, handleSpeechStart, proceedToListening]);

  const openAshleyInterface = useCallback(() => {
    if (isGuidedWorkflowActive()) {
      voiceDiag("openVoiceOverlay skipped — guided workflow active", {
        hypothesisId: "H-B",
        location: "AshleyInterfaceProvider.openAshleyInterface",
      });
      return;
    }

    if (isActiveRef.current) {
      voiceDiag("openVoiceOverlay skipped — already active", {
        hypothesisId: "H-B",
        location: "AshleyInterfaceProvider.openAshleyInterface",
      });
      return;
    }

    ashleyLog("Overlay opened");
    voiceDiag("openVoiceOverlay — Ashley interface opening", {
      hypothesisId: "H-B",
      location: "AshleyInterfaceProvider.openAshleyInterface",
      data: { msSinceGesture: msSinceLastGesture() },
    });
    isActiveRef.current = true;
    promptHandoffRef.current = false;
    setSpeechPlaybackConfirmed(false);
    setPhase(ASHLEY_PHASE.PROMPTING);
    setStatusMessage("Ashley is ready.");
    // Diagnostics must not block greeting TTS (await would leave the iOS gesture).
    void Promise.resolve()
      .then(() => captureEnvironmentSnapshot())
      .catch(() => {});
    beginPrompt();
  }, [beginPrompt]);

  const openAshleyInterfaceRef = useRef(openAshleyInterface);
  openAshleyInterfaceRef.current = openAshleyInterface;

  useEffect(() => {
    void Promise.resolve()
      .then(() => captureEnvironmentSnapshot())
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      voiceDiag("EVENT: cp-ashley-interface-open received", {
        hypothesisId: "H-WIRE",
        location: "AshleyInterfaceProvider.eventListener",
      });
      openAshleyInterfaceRef.current();
    };

    window.addEventListener(ASHLEY_INTERFACE_OPEN_EVENT, handleOpen);
    return () => {
      window.removeEventListener(ASHLEY_INTERFACE_OPEN_EVENT, handleOpen);
    };
  }, []);

  const value = useMemo(
    () => ({
      phase,
      statusMessage,
      closeAshleyInterface,
      isActive: phase !== ASHLEY_PHASE.IDLE,
      speechPlaybackConfirmed,
    }),
    [closeAshleyInterface, phase, speechPlaybackConfirmed, statusMessage],
  );

  return (
    <AshleyInterfaceContext.Provider value={value}>
      {children}
    </AshleyInterfaceContext.Provider>
  );
}

export function useAshleyInterface() {
  const context = useContext(AshleyInterfaceContext);
  if (!context) {
    throw new Error("useAshleyInterface must be used within AshleyInterfaceProvider");
  }
  return context;
}
